import React from 'react';
import { Autocomplete, TextField, Checkbox, Chip } from '@mui/material';

/**
 * Type-to-filter dropdown (MUI Autocomplete) that behaves like the plain <Select> it replaces:
 * you hand it options + the current value(s) and get the value(s) back on change — no option
 * objects to juggle. Supports single and multiple (checkbox + chip) selection.
 *
 * Props:
 *  - options:        array of option objects.
 *  - value:          selected value (single) or array of values (multiple). '' / [] = none.
 *  - onChange:       (newValue) => void — the selected value (single) or array of values (multiple).
 *  - getOptionLabel: option => string shown in the list/field (default: name → label → id).
 *  - getOptionValue: option => the value stored (default: option.id).
 *  - multiple:       true for multi-select with checkboxes + chips.
 *  - placeholder:    shown when nothing is selected (e.g. "All").
 *  - label, helperText, fullWidth, size, and any extra Autocomplete props pass through.
 */
export default function SearchSelect({
  options = [],
  value,
  onChange,
  getOptionLabel = (o) => (o?.name ?? o?.label ?? (o?.id != null ? String(o.id) : '')),
  getOptionValue = (o) => o?.id,
  multiple = false,
  placeholder,
  label,
  helperText,
  fullWidth = true,
  size = 'small',
  disableCloseOnSelect,
  ...rest
}) {
  const sameVal = (a, b) => String(a) === String(b);
  const eq = (o, v) => sameVal(getOptionValue(o), getOptionValue(v));

  if (multiple) {
    const selected = options.filter(
      (o) => Array.isArray(value) && value.some((v) => sameVal(v, getOptionValue(o))));
    return (
      <Autocomplete
        multiple
        disableCloseOnSelect={disableCloseOnSelect ?? true}
        size={size}
        fullWidth={fullWidth}
        options={options}
        value={selected}
        onChange={(e, val) => onChange(val.map(getOptionValue))}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={eq}
        renderOption={(props, o, { selected: sel }) => (
          <li {...props} key={getOptionValue(o)}>
            <Checkbox size="small" checked={sel} sx={{ mr: 1 }} />
            {getOptionLabel(o)}
          </li>
        )}
        renderTags={(val, getTagProps) => val.map((o, index) => (
          <Chip size="small" label={getOptionLabel(o)} {...getTagProps({ index })} key={getOptionValue(o)} />
        ))}
        renderInput={(p) => (
          <TextField {...p} label={label}
            placeholder={selected.length ? '' : placeholder}
            helperText={helperText} />
        )}
        {...rest}
      />
    );
  }

  const selected = options.find((o) => sameVal(getOptionValue(o), value)) || null;
  return (
    <Autocomplete
      size={size}
      fullWidth={fullWidth}
      options={options}
      value={selected}
      onChange={(e, val) => onChange(val ? getOptionValue(val) : '')}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={eq}
      renderInput={(p) => (
        <TextField {...p} label={label} placeholder={placeholder} helperText={helperText} />
      )}
      {...rest}
    />
  );
}
